import * as THREE from 'three';
import { ORBIT_LINE_SEGMENTS, ORBIT_LINE_OPACITY } from './constants';
import { OrbitalParams } from './types';

/**
 * Renders the elliptical orbit path as a visible line loop in the scene.
 * The orbit lies in the XZ plane with Y=0.
 */
export class OrbitRenderer {
  private line: THREE.LineLoop;
  private material: THREE.LineBasicMaterial;
  private segmentCount: number;

  constructor(params: OrbitalParams, segmentCount: number = ORBIT_LINE_SEGMENTS) {
    this.segmentCount = segmentCount;
    this.material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: ORBIT_LINE_OPACITY,
    });

    const geometry = this.createGeometry(params);
    this.line = new THREE.LineLoop(geometry, this.material);
  }

  /**
   * Regenerates the line geometry for new orbital parameters.
   */
  updatePath(params: OrbitalParams): void {
    const oldGeometry = this.line.geometry;
    const newGeometry = this.createGeometry(params);
    this.line.geometry = newGeometry;
    oldGeometry.dispose();
  }

  /**
   * Returns the THREE.LineLoop object for adding to the scene.
   */
  getLine(): THREE.LineLoop {
    return this.line;
  }

  /**
   * Disposes of geometry and material resources.
   */
  dispose(): void {
    this.line.geometry.dispose();
    this.material.dispose();
  }

  /**
   * Creates a BufferGeometry with positions computed along the ellipse.
   * For each of segmentCount evenly-spaced θ values in [0, 2π):
   *   r = a(1 - e²) / (1 + e·cos(θ))
   *   x = r·cos(θ), z = r·sin(θ), y = 0
   */
  private createGeometry(params: OrbitalParams): THREE.BufferGeometry {
    const { semiMajorAxis: a, eccentricity: e } = params;
    const positions = new Float32Array(this.segmentCount * 3);

    const semiLatusRectum = a * (1 - e * e);

    for (let i = 0; i < this.segmentCount; i++) {
      const theta = (i / this.segmentCount) * 2 * Math.PI;
      const r = semiLatusRectum / (1 + e * Math.cos(theta));

      const x = r * Math.cos(theta);
      const z = r * Math.sin(theta);
      const y = 0;

      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geometry;
  }
}
